import * as React from 'react';
import { Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import SoundService, { DEFAULT_MUTE_REQUEST_ID } from '~services/SoundService';
import NotificationService from '~services/NotificationService';
import styles from './MutedIndicator.module.css';

/** Local replacement for @ixicare/ui's NotificationIndicator that keeps the
 *  bell + notification icons in sync with the actual SoundService /
 *  NotificationService state — including silencing driven by the presence
 *  store. The ui-package version reads mute state once at mount and never
 *  updates, so the icon goes stale when presence flips it. */

const useExternal = (subscribe: (cb: () => void) => () => void, getSnapshot: () => boolean) =>
  React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

const MutedIndicator: React.FC = () => {
  const isMuted = useExternal(
    (cb) => SoundService.subscribe(cb),
    () => SoundService.muteRequests.length > 0
  );
  // Notifications fire only when permission is granted AND we're not silenced
  // by presence. Subscribe to both signals.
  const notificationsEnabled = useExternal(
    (cb) => {
      const unsubSilence = NotificationService.subscribe(cb);
      const unsubPerm = NotificationService.onPermissionsChange(cb);
      return () => {
        unsubSilence();
        unsubPerm?.();
      };
    },
    () => NotificationService.isEnabled
  );

  // The user's own mute click owns its request id. Presence-driven mute uses
  // PRESENCE_MUTE_REQUEST_ID and layers on top — they're independent reasons.
  const muteRequestId = React.useRef(DEFAULT_MUTE_REQUEST_ID);

  const handleMuteToggle = () => {
    if (isMuted) {
      SoundService.unmute(muteRequestId.current);
    } else {
      muteRequestId.current = SoundService.mute();
    }
  };

  const handleNotificationToggle = () => {
    if (!NotificationService.isPermissionGranted) {
      NotificationService.enable();
    }
  };

  return (
    <>
      <button className={styles.Indicator} onClick={handleMuteToggle} type="button">
        {isMuted ? <VolumeX size={22} /> : <Volume2 size={22} />}
      </button>
      <button
        className={styles.Indicator}
        onClick={handleNotificationToggle}
        type="button"
        disabled={notificationsEnabled}
      >
        {notificationsEnabled ? <Bell size={22} /> : <BellOff size={22} />}
      </button>
    </>
  );
};

export default MutedIndicator;
